import { createApi } from '@reduxjs/toolkit/query/react'
import getQuery from '../../helpers/api/get-query'
import PrintJobExecutor from '../../types/api/print-job-executor'
import Strapi, {
    EndpointQueryParameters,
    StrapiCreate,
    StrapiSingle,
    StrapiUpdate,
} from '../../types/strapi/strapi'
import getCustomFetchBase from '../custom-fetch-base'

const EXECUTORS_PAGE_SIZE = 5000

export const printJobExecutorsApi = createApi({
    reducerPath: 'printJobExecutorsApi',
    baseQuery: getCustomFetchBase({ baseUrl: import.meta.env.VITE_API_URL }),
    tagTypes: ['PrintJobExecutors'],
    endpoints: builder => ({
        getPrintJobExecutors: builder.query<
            Strapi<PrintJobExecutor>,
            EndpointQueryParameters | void
        >({
            query: getQuery('/print-job-executors'),
            providesTags: ['PrintJobExecutors'],
        }),
        getPrintJobExecutorsAllPages: builder.query<PrintJobExecutor[], EndpointQueryParameters>({
            async queryFn(params, _api, _extraOptions, baseQuery) {
                const fetchPage = (page: number) =>
                    baseQuery(
                        getQuery('/print-job-executors')({
                            ...params,
                            pagination: { page, pageSize: EXECUTORS_PAGE_SIZE },
                        })
                    )

                const first = await fetchPage(1)

                if (first.error) {
                    return { error: first.error }
                }

                const payload = first.data as Strapi<PrintJobExecutor>
                const all = [...(payload.data ?? [])]
                const pageCount = payload.meta?.pagination?.pageCount ?? 1

                if (pageCount <= 1) {
                    return { data: all }
                }

                const rest = await Promise.all(
                    Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2))
                )

                for (const result of rest) {
                    if (result.error) {
                        return { error: result.error }
                    }

                    const pagePayload = result.data as Strapi<PrintJobExecutor>
                    all.push(...(pagePayload.data ?? []))
                }

                return { data: all }
            },
            providesTags: ['PrintJobExecutors'],
        }),
        createPrintJobExecutor: builder.mutation<
            StrapiSingle<PrintJobExecutor>,
            StrapiCreate<PrintJobExecutor>
        >({
            query: body => ({
                url: '/print-job-executors',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PrintJobExecutors'],
        }),
        updatePrintJobExecutor: builder.mutation<
            StrapiSingle<PrintJobExecutor>,
            StrapiUpdate<PrintJobExecutor>
        >({
            query: ({ docId, body }) => ({
                url: `/print-job-executors/${docId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PrintJobExecutors'],
        }),
    }),
})

export const {
    useGetPrintJobExecutorsQuery,
    useGetPrintJobExecutorsAllPagesQuery,
    useCreatePrintJobExecutorMutation,
    useUpdatePrintJobExecutorMutation,
} = printJobExecutorsApi
